import * as React from 'react';

import debounce from 'lodash/debounce';

import type {
    GetPlanComparisons_planComparisons as PlanComparisons,
    GetPlanComparisons_planComparisons_planStubs as PlanStubs,
    GetPlanComparisons_planComparisons_featureGroups as FeatureGroups,
} from 'nutshell-graphql-types';

import {Body} from '../../typography/body';
import {useResizeObserver} from '../../utils/hooks/use-resize-observer';

import {PlanComparisonFloatingButton} from './plan-comparison-floating-button';
import {PlanComparisonTable} from './plan-comparison-table';

import styles from './plan-comparison-table-wrapper.module.css';

const SHOW_BUTTON_THRESHOLD = 500;

type Props = {
    planComparisons: PlanComparisons;
    scrollContainerId: string;
};

export function PlanComparisonTableWrapper(props: Props) {
    const [shouldShowButton, setShouldShowButton] = React.useState<boolean>(true);

    const targetRef = React.useRef(null);
    const scrollContainerRef = React.useRef(null);

    const plans: Array<PlanStubs> = props.planComparisons.planStubs;
    const featureGroups: Array<FeatureGroups> = props.planComparisons.featureGroups;

    const handleComparePlansButtonClick = () => {
        if (targetRef.current) {
            // @ts-expect-error
            targetRef.current.scrollIntoView({behavior: 'smooth'});
        }
    };

    const [tableWidth] = useResizeObserver(targetRef.current);

    React.useLayoutEffect(() => {
        const scrollContainer = document.getElementById(props.scrollContainerId);

        if (scrollContainer) {
            // @ts-expect-error
            scrollContainerRef.current = scrollContainer;
        }
    }, [props.scrollContainerId]);

    React.useEffect(() => {
        // Define scroll handler, which uses debounce to reduce number of times it runs
        const handleScroll = debounce(() => {
            if (targetRef.current) {
                // @ts-expect-error
                const targetRect = targetRef.current.getBoundingClientRect();

                if (targetRect && targetRect.top < SHOW_BUTTON_THRESHOLD) {
                    setShouldShowButton(false);
                } else {
                    setShouldShowButton(true);
                }
            }
        }, 100);

        const scrollContainerElement = scrollContainerRef && scrollContainerRef.current;
        if (scrollContainerElement) {
            // @ts-expect-error
            scrollContainerElement.addEventListener('scroll', handleScroll);
        }

        return () => {
            if (scrollContainerElement) {
                // @ts-expect-error
                scrollContainerElement.removeEventListener('scroll', handleScroll);
            }
        };
    }, [scrollContainerRef, targetRef]);

    return (
        <>
            <div className='pb-16'>
                <Body size='large' isBold={true}>
                    Plan comparison
                </Body>
            </div>
            <div ref={targetRef} className={styles['plan-comparison']}>
                <PlanComparisonTable plans={plans} features={featureGroups} />
            </div>
            {shouldShowButton && tableWidth && (
                <PlanComparisonFloatingButton
                    onClick={handleComparePlansButtonClick}
                    // table width + adjustment for sidebar/padding makes the button centered on the table
                    leftPosition={tableWidth / 2 + 156}
                />
            )}
        </>
    );
}
